import { gql } from "@apollo/client";
import { initApollo } from "../graphql/init";

import {
  FETCH_TRANSLATIONS,
  UPDATE_TRANSLATION,
  DELETE_TRANSLATION,
  LOADING_TRANSLATIONS,
  SET_TRANSLATION_DICTIONARY
} from "./type";

const apolloClient = initApollo();

// GraphQL Queries
const GET_TRANSLATIONS = gql`
  query GetTranslations($page: Int, $limit: Int, $search: String) {
    getTranslations(page: $page, limit: $limit, search: $search) {
      translations {
        id
        word
        meanings {
          id
          pos
          translations
        }
        wasModified
        updatedAt
      }
      total
      page
      limit
      totalPages
    }
  }
`;

export const GET_TRANSLATION = gql`
  query GetTranslation($word: String!) {
    getTranslation(word: $word) {
      word
      meanings {
        pos
        translations
      }
    }
  }
`;

export const GET_TRANSLATIONS_BY_WORDS = gql`
  query GetTranslationsByWords($words: [String!]!) {
    getTranslationsByWords(words: $words) {
      word
      meanings {
        pos
        translations
      }
    }
  }
`;

export const GET_TRANSLATION_DICTIONARY = gql`
  query GetTranslationDictionary {
    getTranslationDictionary
  }
`;

const UPSERT_TRANSLATION = gql`
  mutation UpsertTranslation($inputs: TranslationInput!) {
    upsertTranslation(inputs: $inputs) {
      status
      success
      message
      translation {
        id
        word
        meanings {
          id
          pos
          translations
        }
        wasModified
        updatedAt
      }
    }
  }
`;

const DELETE_TRANSLATION_MUTATION = gql`
  mutation DeleteTranslation($word: String!) {
    deleteTranslation(word: $word) {
      status
      success
      message
    }
  }
`;

const BULK_UPSERT_TRANSLATIONS = gql`
  mutation BulkUpsertTranslations($inputs: BulkTranslationInput!) {
    bulkUpsertTranslations(inputs: $inputs) {
      status
      success
      message
      created
      updated
    }
  }
`;

const INITIALIZE_TRANSLATIONS = gql`
  mutation InitializeTranslations {
    initializeTranslations {
      status
      success
      message
      created
      updated
    }
  }
`;

// Fetch all translations with pagination and search
export const fetchTranslations =
  ({ page = 1, limit = 100, search = "" }, callback) =>
  async (dispatch) => {
    try {
      dispatch({ type: LOADING_TRANSLATIONS, payload: true });

      const { data } = await apolloClient.query({
        query: GET_TRANSLATIONS,
        variables: { page, limit, search },
        fetchPolicy: "network-only"
      });

      const { translations, total, totalPages } = data.getTranslations;

      // Convert array to map for easier access
      const translationMap = {};
      translations.forEach((translation) => {
        translationMap[translation.id] = translation;
      });

      dispatch({
        type: FETCH_TRANSLATIONS,
        payload: {
          translationMap,
          total,
          page,
          limit,
          totalPages
        }
      });

      dispatch({ type: LOADING_TRANSLATIONS, payload: false });

      if (callback) callback();
    } catch (error) {
      console.error("Error fetching translations:", error);
      dispatch({ type: LOADING_TRANSLATIONS, payload: false });
      throw error;
    }
  };

// Fetch translation dictionary (optimized format for use in application)
export const fetchTranslationDictionary = (callback) => async (dispatch) => {
  try {
    const { data } = await apolloClient.query({
      query: GET_TRANSLATION_DICTIONARY,
      fetchPolicy: "network-only"
    });

    const dictionary = JSON.parse(data.getTranslationDictionary);

    dispatch({
      type: SET_TRANSLATION_DICTIONARY,
      payload: { dictionary }
    });

    if (callback) callback(dictionary);
  } catch (error) {
    console.error("Error fetching translation dictionary:", error);
    throw error;
  }
};

// Create or update a translation
export const upsertTranslation =
  (inputs, callback) => async (dispatch, getState) => {
    try {
      const { data } = await apolloClient.mutate({
        mutation: UPSERT_TRANSLATION,
        variables: { inputs }
      });

      const { success, message, translation } = data.upsertTranslation;

      if (success && translation) {
        // Update the translation in the store
        dispatch({
          type: UPDATE_TRANSLATION,
          payload: { translation }
        });

        // Also update the dictionary
        dispatch(fetchTranslationDictionary());

        if (callback) callback({ success: true, translation });
      } else {
        if (callback) callback({ success: false, message });
      }

      return { success, message, translation };
    } catch (error) {
      console.error("Error upserting translation:", error);
      if (callback) callback({ success: false, message: error.message });
      throw error;
    }
  };

// Bulk upsert translations (admin only)
export const bulkUpsertTranslations =
  (translations, callback) => async (dispatch) => {
    try {
      const { data } = await apolloClient.mutate({
        mutation: BULK_UPSERT_TRANSLATIONS,
        variables: { inputs: { translations } }
      });

      const { success, message, created, updated } =
        data.bulkUpsertTranslations;

      if (success) {
        // Refresh translations and dictionary
        dispatch(fetchTranslations({ page: 1, limit: 500 }));
        dispatch(fetchTranslationDictionary());
        if (callback) callback({ success: true, created, updated });
      } else {
        if (callback) callback({ success: false, message });
      }
    } catch (error) {
      console.error("Error bulk upserting translations:", error);
      if (callback) callback({ success: false, message: error.message });
    }
  };

// Delete a translation
export const deleteTranslation = (word, callback) => async (dispatch) => {
  try {
    const { data } = await apolloClient.mutate({
      mutation: DELETE_TRANSLATION_MUTATION,
      variables: { word }
    });

    const { success, message } = data.deleteTranslation;

    if (success) {
      dispatch({
        type: DELETE_TRANSLATION,
        payload: { word }
      });

      // Also update the dictionary
      dispatch(fetchTranslationDictionary());

      if (callback) callback({ success: true });
    } else {
      if (callback) callback({ success: false, message });
    }

    return { success, message };
  } catch (error) {
    console.error("Error deleting translation:", error);
    if (callback) callback({ success: false, message: error.message });
    throw error;
  }
};

// Initialize translations from static dictionary (one-time use, super admin only)
export const initializeTranslations = (callback) => async (dispatch) => {
  try {
    dispatch({ type: LOADING_TRANSLATIONS, payload: true });

    const { data } = await apolloClient.mutate({
      mutation: INITIALIZE_TRANSLATIONS
    });

    const { success, message, created, updated } = data.initializeTranslations;

    dispatch({ type: LOADING_TRANSLATIONS, payload: false });

    if (success) {
      // Fetch all translations after initialization
      dispatch(fetchTranslations({ page: 1, limit: 100 }));
      dispatch(fetchTranslationDictionary());

      if (callback) callback({ success: true, created, updated });
    } else {
      if (callback) callback({ success: false, message });
    }

    return { success, message, created, updated };
  } catch (error) {
    console.error("Error initializing translations:", error);
    dispatch({ type: LOADING_TRANSLATIONS, payload: false });
    if (callback) callback({ success: false, message: error.message });
    throw error;
  }
};
